import React, { useEffect, useState, useCallback, useRef } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { API, Amplify, Auth, graphqlOperation } from "aws-amplify";
import { useAuth } from "react-oidc-context";
import awsExports from "./aws-exports";

// GraphQL Imports
import { userByEmail } from "./graphql/queries";
import {
  getReloadYard,
  customerByEmail,
} from "./graphql/reloadyard/custom-queries";
import {
  updateCustomerMini,
  updateUserMini,
} from "./graphql/reloadyard/custom-mutations";
import { carrierUserByEmail } from "./graphql/carrier/carrier-queries";
import { updateCarrierUserMini } from "./graphql/carrier/carrier-mutations";

// UI Imports
import { Backdrop, CircularProgress, makeStyles } from "@material-ui/core";
import { SnackbarProvider } from "notistack";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

// App Component Imports
import Logout from "./components/Logout";
import { AdminApp } from "./adminApp/AdminApp";
import { CarrierApp } from "./carrierApp/CarrierApp";
import { NoAccessScreen } from "./components/NoAccessScreen";
import { DriverApp } from "./driverApp/DriverApp";
import { CustomerApp } from "./customerApp/CustomerApp";
import { ManagerApp } from "./managerApp/ManagerApp";
import { CrewApp } from "./crewApp/CrewApp";
import { NewYard } from "./registrationApp/NewYard";

// Utilities
import {
  AppType,
  PortalType,
  ReloadYardStatus,
  UserType,
} from "./utils/constants";
import {
  logError,
  retrieveStripeSubscription,
  setupCommodityMaps,
  setupLocationMaps,
} from "./utils/appUtils";
import {
  getCognitoDomain,
  signOutRedirect as signOutRedirectUtil,
} from "./utils/authUtils";
import { ensureProduktlyToken } from "./utils/produktlyUtils";
import {
  buildCarrierSession,
  isCarrierUserRecord,
  normalizeCarrierUserType,
  selectCarrierJunction,
} from "./utils/carrierUserUtils";
import { useUser } from "./authenticatorApp/UserContext";
import { PhotoTransferProvider } from "./context/PhotoTransferContext";
import PhotoTransferPanel from "./components/common/PhotoTransferPanel";
import { ColorMapProvider } from "./context/ColorMapContext";

// Passkey Components & Hooks
import PasskeySetupDialog from "./components/passkey/PasskeySetupDialog";
import { usePasskeyCheck } from "./hooks/usePasskeyCheck";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
}));

// Store token and expiry state globally for Amplify / appUtils to access.
// Both are initialised here so neither is ever undefined before the first effect runs.
window.getAuthToken = () => null;
window.isAuthTokenExpired = () => false;

// --- HELPER FUNCTIONS ---

const doGetReloadYard = async (id) => {
  const response = await API.graphql({
    query: getReloadYard,
    variables: { id },
  });

  const reloadYard = response.data.getReloadYard;
  const items = reloadYard.aisles?.items ?? [];
  const filteredItems = items.filter((itm) => itm.active === "true");
  if (reloadYard.aisles) reloadYard.aisles.items = filteredItems;
  return reloadYard;
};

const getPortalType = (appUser) => {
  let portalType = localStorage.getItem("portalType");
  if (!portalType) {
    if (appUser.type === UserType.CUSTOMER || appUser.type === UserType.ADMIN)
      portalType = PortalType.MANAGER;
    else
      portalType =
        appUser.type !== UserType.CREW ? PortalType.MANAGER : PortalType.CREW;
    try {
      localStorage.setItem("portalType", portalType);
    } catch (err) {
      logError("getPortalType", err, false);
    }
  }
  return portalType;
};

const setupAdminData = (appUser, authData) => {
  // setup ADMIN
  appUser.portalType = getPortalType(appUser);
  return { ...appUser, appType: AppType.ADMIN, cognitoId: authData.username };
};

const setupUserData = async (appUser, authData) => {
  const reloadYard = await doGetReloadYard(appUser.reloadYardId);

  if (reloadYard?.status === ReloadYardStatus.TERMINATED) {
    return null;
  }
  const subscription = await retrieveStripeSubscription(reloadYard);
  setupLocationMaps(reloadYard);
  setupCommodityMaps(reloadYard);
  appUser.portalType = getPortalType(appUser);

  // transform ACL Permissions
  appUser.permissionList = {};
  appUser.acl?.forEach((itm) => {
    appUser.permissionList[itm] = true;
  });

  return {
    ...appUser,
    reloadYard,
    subscription,
    appType: AppType.YARD,
    cognitoId: authData.username,
  };
};

const setupCustomerData = async (appUser, authData) => {
  // Filtered here rather than in getUserByEmail, which used to null the user
  // when nothing was active. That made hydrate() treat a customer whose accounts
  // had all been deactivated as an unknown email and drop them into the new-yard
  // signup flow. Deciding access next to the account selection — as
  // setupCarrierData does with its junctions — keeps that case a blocked screen.
  const accessibleAccounts = (appUser.reloadYardAccounts?.items ?? []).filter(
    (account) => account.active === "true",
  );

  if (accessibleAccounts.length === 0) {
    return {
      ...appUser,
      appType: AppType.CUSTOMER,
      blocked: true,
      cognitoId: authData.username,
    };
  }

  let reloadYardId = localStorage.getItem("reloadYardId");
  let reloadYardAccount = accessibleAccounts.find(
    (account) => account.reloadYardId === reloadYardId,
  );

  if (!reloadYardAccount) {
    reloadYardAccount = accessibleAccounts[0];
    reloadYardId = reloadYardAccount.reloadYardId;
    if (reloadYardId) localStorage.setItem("reloadYardId", reloadYardId);
  }

  const reloadYard = await doGetReloadYard(reloadYardId);
  setupLocationMaps(reloadYard);
  setupCommodityMaps(reloadYard);
  appUser.portalType = getPortalType(appUser);
  appUser.permissionList = {};
  reloadYardAccount.acl?.forEach((itm) => {
    appUser.permissionList[itm] = true;
  });

  return {
    ...appUser,
    fullName: appUser.firstName + " " + appUser.lastName,
    // Inactive accounts stay out of the portal's account switcher, which is what
    // getUserByEmail's write-back used to guarantee.
    reloadYardAccounts: {
      ...appUser.reloadYardAccounts,
      items: accessibleAccounts,
    },
    reloadYard,
    reloadYardId,
    appType: AppType.CUSTOMER,
    accountId: reloadYardAccount.accountId,
    accountName: reloadYardAccount.accountName,
    account: reloadYardAccount.account,
    cognitoId: authData.username,
  };
};

const setupCarrierData = async (appUser, authData) => {
  const storedReloadYardId = localStorage.getItem("reloadYardId");
  // The yard alone cannot name the session to resume: a person can dispatch for
  // two carriers out of the same yard, one junction each.
  const storedCarrierId = localStorage.getItem("carrierId");
  const { reloadYardCarrier, unusableTypes } = selectCarrierJunction(
    appUser.reloadYardCarriers?.items,
    storedReloadYardId,
    storedCarrierId,
  );

  // Reported even when another junction carried the user through: the row is
  // still wrong, and the yard it belongs to is silently unreachable until it is
  // fixed.
  unusableTypes.forEach((rycu) =>
    logError(
      "setupCarrierData",
      new Error(
        `Unrecognised carrier user type "${rycu.type}" on junction ${rycu.id}`,
      ),
      false,
    ),
  );

  if (!reloadYardCarrier) {
    return {
      ...appUser,
      appType: AppType.CARRIER,
      blocked: true,
      cognitoId: authData.username,
    };
  }

  const reloadYardId = reloadYardCarrier.reloadYardId;
  const carrierId = reloadYardCarrier.carrierId;
  if (reloadYardId !== storedReloadYardId || carrierId !== storedCarrierId) {
    try {
      localStorage.setItem("reloadYardId", reloadYardId);
      localStorage.setItem("carrierId", carrierId);
    } catch (err) {
      logError("setupCarrierData", err, false);
    }
  }

  const reloadYard = await doGetReloadYard(reloadYardId);
  setupCommodityMaps(reloadYard);

  return {
    ...appUser,
    fullName: appUser.firstName + " " + appUser.lastName,
    reloadYard,
    // type, permissionList, appType and carrierId all follow from the junction —
    // shared with the dashboard's yard switcher, which applies the same
    // derivation to whichever junction the user picks.
    ...buildCarrierSession(reloadYardCarrier),
    cognitoId: authData.username,
  };
};

const getUserByEmail = async (email) => {
  const lowerEmail = email.toLowerCase();
  const [userResponse, customerResponse, carrierUserResponse] =
    await Promise.all([
      API.graphql(graphqlOperation(userByEmail, { email: lowerEmail })),
      API.graphql(graphqlOperation(customerByEmail, { email: lowerEmail })),
      API.graphql(graphqlOperation(carrierUserByEmail, { email: lowerEmail })),
    ]);

  let user;
  if (userResponse.data.userByEmail.items?.length > 0) {
    user = userResponse.data.userByEmail.items[0];
  } else if (customerResponse.data.customerByEmail.items?.length > 0) {
    // Account accessibility is setupCustomerData's call. Returning null here for
    // a customer with nothing active made them indistinguishable from an unknown
    // email, and hydrate() sends those to the new-yard signup flow.
    user = customerResponse.data.customerByEmail.items[0];
    user.type = UserType.CUSTOMER;
  } else if (carrierUserResponse.data.carrierUserByEmail.items?.length > 0) {
    user = carrierUserResponse.data.carrierUserByEmail.items[0];
    // Provisional only: items[0] is an arbitrary junction, so this may not be
    // the type of the one the user actually logs in under. setupCarrierData
    // re-derives `type` from the junction it selects, and routing goes through
    // isCarrierUserRecord rather than this value — nothing here decides which
    // portal the user reaches. Still normalised so the field is canonical for
    // anything reading it before that point (legacy rows store "DISPATCH").
    user.type = normalizeCarrierUserType(
      user.reloadYardCarriers?.items?.[0]?.type,
    );
  }

  return user;
};

const updateUser = (user, cognitoId) => {
  if (user.cognitoId) return;
  try {
    const userToUpdate = { ...user, cognitoId };
    let mutation;
    if (user.type === UserType.CUSTOMER) mutation = updateCustomerMini;
    // Structural, not type-based: an unrecognised junction type must not aim a
    // carrier user's id at the User table.
    else if (isCarrierUserRecord(user)) mutation = updateCarrierUserMini;
    else mutation = updateUserMini;
    delete userToUpdate.permissionList;
    delete userToUpdate.reloadYardAccounts;
    delete userToUpdate.reloadYardCarriers;
    delete userToUpdate.type;
    delete userToUpdate.createdAt;
    delete userToUpdate.updatedAt;

    API.graphql(graphqlOperation(mutation, { input: { ...userToUpdate } }));
  } catch (err) {
    logError("src:App:updateUser", err, false);
  }
};

// Why an authenticated user has no portal. Both cases are set by the setup*
// functions as `blocked: true`, so the reason has to come from which portal the
// user belongs to rather than from the flag.
const NoAccessReason = {
  SUSPENDED: {
    title: "Access Suspended",
    message:
      "Your account access has been suspended.\nPlease contact your yard administrator.",
  },
  NO_ACCOUNT: {
    title: "No Active Account",
    message:
      "None of your accounts are active.\nPlease contact your yard administrator.",
  },
};

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

// --- MAIN COMPONENT ---

function App({ adminHistory }) {
  const auth = useAuth();

  const { appUser, setAppUser } = useUser();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const amplifyConfigured = useRef(false);
  const credentialsExchanged = useRef(false);
  const hydrationCompleted = useRef(false); // Prevent re-hydration on token refresh
  const initialUserId = useRef(null); // Track initial user ID
  const eventListenersRegistered = useRef(false); // Prevent duplicate event listener registrations
  const isSigningOut = useRef(false); // Prevent signinRedirect from racing with signout
  const produktlyTokenFetched = useRef(false); // Avoid duplicate produktly token fetches

  // Use the passkey check hook
  const { showDialog, setShowDialog } = usePasskeyCheck(
    auth.user?.access_token,
    auth.isAuthenticated,
    awsExports.aws_project_region,
  );

  // Unified Logout — React-specific cleanup, then delegate to authUtils
  const signOutRedirect = useCallback(() => {
    if (isSigningOut.current) return;
    isSigningOut.current = true;
    setAppUser(null);
    credentialsExchanged.current = false;
    produktlyTokenFetched.current = false;
    auth.removeUser();
    signOutRedirectUtil();
  }, [auth, setAppUser]);

  // Set global logout function for 401 error handling
  useEffect(() => {
    const { setGlobalLogoutFunction } = require("./utils/appUtils");
    setGlobalLogoutFunction(signOutRedirect);
  }, [signOutRedirect]);

  // Initialize/get token immediately when user is authenticated (fixes hot reload issue)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.id_token) {
      // Always update token function when user/token changes (including hot reload)
      window.getAuthToken = () => auth.user.id_token;
      // Expose token expiry state so appUtils can distinguish expiry from permission errors
      window.isAuthTokenExpired = () => auth.user?.expired === true;
    } else if (!auth.isAuthenticated) {
      // Clear token when not authenticated
      window.getAuthToken = () => null;
      window.isAuthTokenExpired = () => true;
      produktlyTokenFetched.current = false;
    }
    // auth.user?.expires_at is a stable number set at token-issue time; React can
    // reliably compare it. auth.user?.expired is a computed getter so React cannot
    // reliably detect its change without a stable primitive in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user?.id_token, auth.user?.expires_at]);

  // Fetch Produktly token once after login and app-user hydration.
  useEffect(() => {
    const cacheProduktlyTokenOnLogin = async () => {
      if (
        !auth.isAuthenticated ||
        !auth.user?.access_token ||
        !appUser ||
        produktlyTokenFetched.current
      ) {
        return;
      }

      try {
        await ensureProduktlyToken(auth.user.access_token);
        produktlyTokenFetched.current = true;
      } catch (error) {
        logError("App:ensureProduktlyToken", error, false);
      }
    };

    cacheProduktlyTokenOnLogin();
  }, [auth.isAuthenticated, auth.user?.access_token, appUser]);

  // When not authenticated, redirect: ?signUp → Cognito signup, otherwise → sign in
  // Skip when on /logout so Logout.js can redirect to Cognito logout (avoid signinRedirect race)
  // Skip when signout is in progress to avoid racing with the logout redirect
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated) return;
    if (window.location.pathname === "/logout") return;
    if (isSigningOut.current) return;

    const searchParams = new URLSearchParams(window.location.search);
    const wantsSignUp =
      searchParams.has("signup") || searchParams.has("signUp");
    if (wantsSignUp) {
      const clientId = awsExports.aws_user_pools_web_client_id;
      const cognitoDomain = getCognitoDomain();
      const redirectUri = window.location.origin;
      const scopes = "email openid phone profile aws.cognito.signin.user.admin";
      window.location.href = `${cognitoDomain}/signup?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&response_type=code&scope=${encodeURIComponent(scopes)}`;
    } else {
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth]);

  // Configure Amplify: JWT for GraphQL, AWS Credentials for REST APIs
  useEffect(() => {
    const configureAndExchange = async () => {
      if (
        auth.isAuthenticated &&
        auth.user?.id_token &&
        !amplifyConfigured.current
      ) {
        // console.log(
        //   new Date().toISOString(),
        //   "Configuring Amplify: JWT for GraphQL, AWS Credentials for REST APIs...",
        // );

        // Ensure token is set (should already be set by previous effect, but double-check)
        window.getAuthToken = () => auth.user.id_token;

        // Configure Amplify: Use JWT tokens (OPENID_CONNECT) for GraphQL
        Amplify.configure({
          ...awsExports,
          aws_appsync_authenticationType: "OPENID_CONNECT",
          API: {
            graphql_endpoint: awsExports.aws_appsync_graphqlEndpoint,
            graphql_headers: async () => {
              const token = window.getAuthToken ? window.getAuthToken() : null;
              if (token) {
                return {
                  Authorization: `Bearer ${token}`,
                };
              }
              // If no token, don't send Authorization header (will likely get 401, but that's handled)
              return {};
            },
            endpoints: awsExports.aws_cloud_logic_custom?.map((endpoint) => ({
              name: endpoint.name,
              endpoint: endpoint.endpoint,
              region: endpoint.region,
            })),
          },
        });

        // Intercept API errors globally
        // Note: API.graphql can return either a Promise (queries/mutations) or Observable (subscriptions)
        const originalGraphql = API.graphql;
        API.graphql = (...args) => {
          const result = originalGraphql.apply(API, args);
          const { handleUnauthorizedError } = require("./utils/appUtils");

          // Check if result is an Observable (has subscribe method) - for subscriptions
          // For subscriptions, return as-is and let the subscription handle errors
          // We'll handle subscription errors in the subscription's error callback
          if (result && typeof result.subscribe === "function") {
            // Return Observable as-is - subscriptions will handle errors in their error callbacks
            return result;
          }

          // For Promises (queries/mutations), wrap with error handling
          if (result && typeof result.then === "function") {
            return result.catch(async (error) => {
              const handled = handleUnauthorizedError(error);
              // Track whether a refresh was in progress before we await,
              // because this is the only signal for "retry after refresh".
              // A synchronous false means "not a token-expiry 401" (permission error) — no retry.
              // A Promise<false> means "refresh was in progress and succeeded" — retry.
              const refreshWasInProgress = handled instanceof Promise;
              const wasHandled = refreshWasInProgress ? await handled : handled;

              if (wasHandled) {
                throw new Error("Unauthorized - Session expired");
              }

              // Only retry if the refresh we waited for completed successfully.
              // Do NOT check isTokenRefreshInProgress() here: a new renewal cycle
              // may have started immediately after refresh A completed, but refresh A's
              // token is still valid and the retry should proceed.
              if (refreshWasInProgress && !wasHandled) {
                console.log(
                  new Date().toISOString(),
                  "Retrying GraphQL request after token refresh...",
                );
                try {
                  return await originalGraphql.apply(API, args);
                } catch (retryError) {
                  const retryHandled = handleUnauthorizedError(retryError);
                  const retryWasHandled =
                    retryHandled instanceof Promise
                      ? await retryHandled
                      : retryHandled;
                  if (retryWasHandled) {
                    throw new Error("Unauthorized - Session expired");
                  }
                  throw retryError;
                }
              }

              throw error;
            });
          }

          // Fallback: return as-is
          return result;
        };

        // Intercept REST API errors
        ["post", "get", "put", "del"].forEach((method) => {
          const originalMethod = API[method];
          API[method] = async (...args) => {
            try {
              return await originalMethod.apply(API, args);
            } catch (error) {
              const { handleUnauthorizedError } = require("./utils/appUtils");
              const handled = handleUnauthorizedError(error);
              // A Promise return means a refresh was in progress (only case where retry makes sense).
              // A synchronous false means a permission error — must not retry.
              const refreshWasInProgress = handled instanceof Promise;
              const wasHandled = refreshWasInProgress ? await handled : handled;

              if (wasHandled) {
                throw new Error("Unauthorized - Session expired");
              }

              // Only retry if the refresh we waited for completed successfully.
              // Do NOT check isTokenRefreshInProgress() — see GraphQL interceptor comment.
              if (refreshWasInProgress && !wasHandled) {
                console.log(
                  new Date().toISOString(),
                  `Retrying ${method.toUpperCase()} request after token refresh...`,
                );
                try {
                  return await originalMethod.apply(API, args);
                } catch (retryError) {
                  const retryHandled = handleUnauthorizedError(retryError);
                  const retryWasHandled =
                    retryHandled instanceof Promise
                      ? await retryHandled
                      : retryHandled;
                  if (retryWasHandled) {
                    throw new Error("Unauthorized - Session expired");
                  }
                  throw retryError;
                }
              }

              throw error;
            }
          };
        });

        amplifyConfigured.current = true;
        // console.log(new Date().toISOString(), "Amplify configuration complete");

        // Exchange OIDC ID Token for AWS Credentials
        if (!credentialsExchanged.current) {
          try {
            const region = awsExports.aws_project_region;
            const userPoolId = awsExports.aws_user_pools_id;
            const providerKey = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;

            await Auth.federatedSignIn(
              providerKey,
              {
                token: auth.user.id_token,
                expires_at: auth.user.expires_at,
              },
              {
                name: auth.user.profile.name || "User",
                email: auth.user.profile.email,
              },
            );

            // const creds = await Auth.currentCredentials();
            // console.log(
            //   new Date().toISOString(),
            //   "AWS Credentials obtained via Identity Pool",
            //   creds.identityId,
            // );

            credentialsExchanged.current = true;
          } catch (err) {
            console.error("Error exchanging token for AWS Credentials", err);
          }
        }
      }
    };

    configureAndExchange();
    // Only depend on isAuthenticated - NOT on auth.user to prevent re-running on token refresh
    // We access auth.user inside the effect but don't depend on it, so it won't re-run when token refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  useEffect(() => {
    const handleOffline = () => setOpen(true);
    const handleOnline = () => setOpen(false);

    setOpen(!navigator.onLine);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // User Hydration (only on initial load, not on token refresh)
  useEffect(() => {
    async function hydrate() {
      // Get current user ID (stable identifier that doesn't change on token refresh)
      const currentUserId =
        auth.user?.profile?.sub || auth.user?.profile?.["cognito:username"];

      // Only hydrate if:
      // 1. User is authenticated
      // 2. Token exists
      // 3. Amplify is configured
      // 4. AppUser doesn't exist yet
      // 5. Hydration hasn't completed yet
      // 6. This is the same user (or initial user ID is not set yet)
      if (
        auth.isAuthenticated &&
        auth.user?.id_token &&
        amplifyConfigured.current &&
        !appUser &&
        !hydrationCompleted.current &&
        currentUserId &&
        (initialUserId.current === null ||
          initialUserId.current === currentUserId)
      ) {
        // console.log(new Date().toISOString(), "Starting hydration...");
        hydrationCompleted.current = true;
        initialUserId.current = currentUserId; // Store the user ID

        await new Promise((resolve) => setTimeout(resolve, 200));

        try {
          const email = auth.user.profile.email;
          const cognitoId =
            auth.user.profile["cognito:username"] || auth.user.profile.sub;

          // console.log(
          //   new Date().toISOString(),
          //   "Fetching user data for:",
          //   email,
          // );
          const dbUser = await getUserByEmail(email);
          let finalUser;

          // console.log(
          //   new Date().toISOString(),
          //   "dbUser",
          //   dbUser,
          //   "type",
          //   dbUser?.type,
          // );

          // Sign out inactive users (except Transloads admins)
          if (
            dbUser?.active === "false" &&
            !dbUser?.email?.endsWith("@transload.co")
          ) {
            console.log(
              new Date().toISOString(),
              "Inactive user detected, signing out...",
            );
            signOutRedirect();
            return;
          }

          if (dbUser) {
            updateUser(dbUser, cognitoId);
            if (dbUser.type === UserType.CUSTOMER)
              finalUser = await setupCustomerData(dbUser, {
                username: cognitoId,
              });
            // Every carrier user goes through setupCarrierData, which selects the
            // junction and validates its type. Branching on dbUser.type instead
            // would strand anyone whose items[0] junction carries a type this
            // build does not know, bypassing the blocked screen written for it.
            else if (isCarrierUserRecord(dbUser))
              finalUser = await setupCarrierData(dbUser, {
                username: cognitoId,
              });
            else if (dbUser.reloadYardId)
              finalUser = await setupUserData(dbUser, { username: cognitoId });
            else if (dbUser.type === UserType.ADMIN)
              finalUser = setupAdminData(dbUser, { username: cognitoId });
          } else {
            finalUser = {
              ...auth.user.profile,
              appType: AppType.REGISTRATION,
              cognitoId: cognitoId,
            };
          }

          if (dbUser && !finalUser) {
            if (dbUser.reloadYardId) {
              // setupUserData returns null for a terminated yard — expected.
              console.log(
                new Date().toISOString(),
                "Terminated yard detected, signing out...",
              );
            } else {
              // No setup function claimed the record, so no portal exists to
              // route to and there is nothing to render a no-access screen from.
              // Logged as an error because it means bad data, not a normal
              // state: attributing every cause here to a terminated yard is what
              // hid the carrier junction-type routing gap.
              logError(
                "src:App:hydrate",
                new Error(
                  `No portal for user ${dbUser.id} (type "${dbUser.type}"), signing out`,
                ),
                false,
              );
            }
            signOutRedirect();
            return;
          }

          // console.log(
          //   new Date().toISOString(),
          //   "Hydration successful",
          //   finalUser,
          // );
          window.appUser = finalUser;
          setAppUser(finalUser);
        } catch (err) {
          console.error("Hydration Failed", err);
          hydrationCompleted.current = false; // Reset on error so it can retry
          initialUserId.current = null; // Reset user ID on error
        }
      }
    }

    hydrate();
    // Only depend on isAuthenticated and appUser - NOT on auth.user to prevent re-running on token refresh
    // We access auth.user inside the effect but don't depend on it, so it won't re-run when token refreshes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, appUser, setAppUser]);

  useEffect(() => {
    // Prevent duplicate event listener registrations
    if (eventListenersRegistered.current || !auth.events) {
      return;
    }

    // With automaticSilentRenew: true, the library handles renewal automatically
    const onTokenExpiring = () => {
      console.log(
        new Date().toISOString(),
        `Automatic silent renew will refresh token.`,
      );
      // Mark that token refresh has started
      const { startTokenRefresh } = require("./utils/appUtils");
      startTokenRefresh();
    };

    // 2. Triggered when token is successfully refreshed (by automatic silent renew)
    const onUserLoaded = async (user) => {
      // Guard: oidc-client-ts can fire this event without a user in some edge cases.
      // A null user means the refresh did NOT succeed — treat it as a failure so that:
      //  • pending requests waiting on the refresh Promise are rejected (not resolved),
      //  • the retry path in handleUnauthorizedError falls into .catch() → logout,
      //  • isTokenRefreshing is cleared and token globals reflect the invalid state.
      if (!user) {
        console.warn(
          "onUserLoaded fired with null/undefined user — treating as refresh failure",
        );
        window.getAuthToken = () => null;
        window.isAuthTokenExpired = () => true;
        const { failTokenRefresh } = require("./utils/appUtils");
        failTokenRefresh(
          new Error("onUserLoaded received null user — refresh failed"),
        );
        signOutRedirect();
        return;
      }

      // console.log(
      //   new Date().toISOString(),
      //   "Token refreshed silently via automatic renew - no page reload.",
      // );
      // Update token and expiry state atomically so isUnauthorizedError never
      // sees hasToken=true + tokenExpired=true during the React re-render gap.
      window.getAuthToken = () => user.id_token;
      window.isAuthTokenExpired = () => user?.expired === true;
      // Re-exchange new id_token for AWS credentials so REST/S3 and credential refresh use the new token.
      // Amplify warns if we call federatedSignIn when a user is already signed in, so sign out from
      // Amplify first (local only; does not touch OIDC session), then federatedSignIn with the new token.
      try {
        await Auth.signOut(); // Clear Amplify's federated session only
        const region = awsExports.aws_project_region;
        const userPoolId = awsExports.aws_user_pools_id;
        const providerKey = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
        await Auth.federatedSignIn(
          providerKey,
          {
            token: user.id_token,
            expires_at: user.expires_at,
          },
          {
            name: user.profile?.name || "User",
            email: user.profile?.email,
          },
        );
        // console.log(
        //   new Date().toISOString(),
        //   "Re-exchanged refreshed token for AWS Credentials",
        // );
      } catch (err) {
        console.error("Re-exchange after token refresh failed", err);
      }
      // Mark that token refresh has completed successfully
      const { completeTokenRefresh } = require("./utils/appUtils");
      completeTokenRefresh();
      // Ensure hydration doesn't run again for the same user
      const newUserId =
        user?.profile?.sub || user?.profile?.["cognito:username"];
      if (newUserId && initialUserId.current === null) {
        initialUserId.current = newUserId;
      }
    };

    // 3. Triggered if token actually dies (User ignored dialog)
    // OR if the Refresh Token (60m) is totally dead
    const onTokenExpired = () => {
      console.log(new Date().toISOString(), "Token expired completely.");
      // Mark that token refresh has failed
      const { failTokenRefresh } = require("./utils/appUtils");
      failTokenRefresh(new Error("Token expired completely"));
      signOutRedirect();
    };

    // 4. Triggered if silent refresh fails specifically
    const onSilentRenewError = (error) => {
      console.error("Silent renew error (likely 60m limit reached)", error);
      // Mark that token refresh has failed
      const { failTokenRefresh } = require("./utils/appUtils");
      failTokenRefresh(error);
      signOutRedirect();
    };

    // Register event listeners only once
    auth.events.addAccessTokenExpiring(onTokenExpiring);
    auth.events.addUserLoaded(onUserLoaded);
    auth.events.addAccessTokenExpired(onTokenExpired);
    auth.events.addSilentRenewError(onSilentRenewError);

    eventListenersRegistered.current = true;

    return () => {
      if (auth.events) {
        auth.events.removeAccessTokenExpiring(onTokenExpiring);
        auth.events.removeUserLoaded(onUserLoaded);
        auth.events.removeAccessTokenExpired(onTokenExpired);
        auth.events.removeSilentRenewError(onSilentRenewError);
      }
      eventListenersRegistered.current = false;
    };
  }, [auth.events, signOutRedirect]);

  const handleClose = (evt, reason) => {
    if (reason === "clickaway") return;
    if (!navigator.onLine) return;
    setOpen(false);
  };

  const offlineSnackbar = (
    <Snackbar open={open} onClose={handleClose}>
      <Alert severity="error">Internet connection lost</Alert>
    </Snackbar>
  );

  // Loading state
  if (auth.isLoading || (auth.isAuthenticated && !appUser)) {
    return (
      <>
        <Backdrop className={classes.backdrop} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {offlineSnackbar}
      </>
    );
  }

  // Unauthenticated state: redirect by URL (?signUp) to sign up, otherwise sign in
  if (!auth.isAuthenticated) {
    return (
      <>
        <Backdrop className={classes.backdrop} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {offlineSnackbar}
      </>
    );
  }

  // Authenticated & Hydrated State
  return (
    <BrowserRouter>
      {offlineSnackbar}

      {/* Passkey Setup Dialog */}
      <PasskeySetupDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSetup={() => {
          // Optional: Add analytics tracking here
          console.log(new Date().toISOString(), "User chose to set up passkey");
        }}
      />
      <Switch>
        <Route exact path="/logout" render={() => <Logout />} />
        <Route
          path="/"
          render={(props) => {
            const renderPortal = () => {
              const user = window.appUser;
              if (user?.appType === AppType.YARD) {
                return user?.type === UserType.CREW ? (
                  <CrewApp {...props} adminHistory={adminHistory} />
                ) : (
                  <ManagerApp {...props} adminHistory={adminHistory} />
                );
              }
              const noAccess = ({ title, message }) => (
                <NoAccessScreen
                  title={title}
                  message={message}
                  onSignOut={signOutRedirect}
                />
              );
              if (user?.appType === AppType.CARRIER) {
                if (user?.blocked) return noAccess(NoAccessReason.SUSPENDED);
                return <CarrierApp {...props} adminHistory={adminHistory} />;
              }
              if (user?.appType === AppType.DRIVER) {
                if (user?.blocked) return noAccess(NoAccessReason.SUSPENDED);
                return <DriverApp {...props} adminHistory={adminHistory} />;
              }
              if (user?.appType === AppType.CUSTOMER) {
                if (user?.blocked) return noAccess(NoAccessReason.NO_ACCOUNT);
                return <CustomerApp {...props} adminHistory={adminHistory} />;
              }
              if (user?.appType === AppType.ADMIN)
                return <AdminApp {...props} adminHistory={adminHistory} />;
              if (user?.appType === AppType.REGISTRATION)
                return (
                  <SnackbarProvider maxSnack={3}>
                    <NewYard {...props} />
                  </SnackbarProvider>
                );
              return null;
            };

            return (
              <ColorMapProvider>
                <PhotoTransferProvider>
                  <>
                    {renderPortal()}
                    <PhotoTransferPanel />
                  </>
                </PhotoTransferProvider>
              </ColorMapProvider>
            );
          }}
        />
      </Switch>
    </BrowserRouter>
  );
}

export default App;
